/**
 * Phase 8 T-14 §13 F (original) / T-17 §23.B (extended) — per-component
 * toast hook.
 *
 * Renders a single bottom-right toast in the component's JSX subtree.
 * Backward-compatible with all existing call-sites that use
 * `kind: 'success' | 'error'`; the new `variant` prop is preferred for new
 * code.
 *
 * For a global multi-stack toast (max 3 simultaneous) use the
 * `useGlobalToast()` hook from `@shared/components/Toast/ToastProvider`
 * which is wired at the app root.
 *
 * Usage (unchanged for existing callers):
 *   const { toast, showToast } = useToast();
 *   showToast({ title: 'Saved', body: 'Changes applied.' });
 *   showToast({ title: 'Failed', kind: 'error' });
 *   // new:
 *   showToast({ title: 'Note', variant: 'info' });
 *   ...
 *   {toast}  ← drop into your JSX
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createElement } from 'react';
import type { ReactElement } from 'react';
import { Toast } from '@shared/components/Toast/Toast';
import type { ToastItem, ToastVariant } from '@shared/components/Toast/Toast';

export interface ToastOptions {
  title: string;
  body?: string;
  /**
   * Preferred variant API (§23.B).
   * @default 'success'
   */
  variant?: ToastVariant;
  /**
   * Legacy alias for `variant`. `'success' | 'error'`.
   * @deprecated Use `variant` instead.
   */
  kind?: 'success' | 'error';
  /** Override auto-dismiss duration (ms). Defaults: success/info/warning=4200, error=7000. */
  duration?: number;
}

interface ToastState extends ToastItem {
  duration: number;
}

let _counter = 0;

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4200,
  info: 4200,
  warning: 4200,
  error: 7000,
};

export function useToast(): {
  toast: ReactElement | null;
  showToast: (opts: ToastOptions) => void;
} {
  const [current, setCurrent] = useState<ToastState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setCurrent(null);
  }, []);

  const showToast = useCallback(
    (opts: ToastOptions) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      // Resolve variant (support legacy `kind` alias).
      const variant: ToastVariant =
        opts.variant ?? (opts.kind === 'error' ? 'error' : 'success');

      const duration = opts.duration ?? DEFAULT_DURATION[variant];
      const id = ++_counter;

      setCurrent({ id, title: opts.title, body: opts.body, variant, duration });
      timerRef.current = setTimeout(dismiss, duration);
    },
    [dismiss],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const toast: ReactElement | null = current
    ? createElement(
        'div',
        {
          key: current.id,
          className: 'fixed bottom-6 right-6 z-[9999]',
        },
        createElement(Toast, { item: current, onDismiss: dismiss }),
      )
    : null;

  return { toast, showToast };
}
